import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import * as PhosphorIcons from '@phosphor-icons/react';
import { LEGAL_BUSINESS_NAME, LEGAL_PAN, LEGAL_GSTIN, LEGAL_ADDRESS } from '../utils/legalInfo';

const { 
  Play, 
  Lightning,
  Users, 
  MapPin: Storefront,
  Clock,
  CalendarBlank: Calendar,
  Megaphone,
  ShieldCheck,
  CheckCircle,
  ArrowRight,
  Sparkle,
  MusicNotes
} = PhosphorIcons;

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // If a session is already established in this browser (e.g. user returned
  // to the landing page in a new tab, or clicked the logo from their
  // dashboard), don't make them log in again — go straight to the app.
  // `user === false` means "checked and not authenticated"; `null` means
  // "still checking", so only redirect when we have a concrete user object.
  useEffect(() => {
    if (user && user !== false) {
      if (user.role === 'kiosk' && user.store_id) navigate(`/kiosk/${user.store_id}`, { replace: true });
      else navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const features = [
    {
      icon: <Clock size={32} weight="bold" className="text-yellow-400" />,
      title: "Advanced Dayparting",
      description: "Schedule different music vibes throughout the day. Morning calm, afternoon energy, evening relaxation - all automated."
    },
    {
      icon: <Calendar size={32} weight="bold" className="text-pink-400" />,
      title: "Special Occasions",
      description: "Pre-schedule holiday playlists for Diwali, Christmas, New Year. Your music adapts to every celebration."
    },
    {
      icon: <Users size={32} weight="bold" className="text-blue-400" />,
      title: "Multi-Location Control",
      description: "Manage music across all your outlets from one dashboard. Push updates to specific regions or all stores instantly."
    },
    {
      icon: <Megaphone size={32} weight="bold" className="text-green-400" />,
      title: "In-Store Announcements",
      description: "Inject promotional messages between tracks. 'Buy 1 Get 1' announcements at perfect intervals."
    },
    {
      icon: <Play size={32} weight="bold" className="text-purple-400" />,
      title: "Auto-Play Kiosk Mode",
      description: "Set it and forget it. Stores play music automatically based on schedule, even if internet drops."
    },
    {
      icon: <ShieldCheck size={32} weight="bold" className="text-red-400" />,
      title: "Legal Compliance",
      description: "Built-in licensing reminders and documentation storage. Stay compliant with PPL, PRS requirements."
    }
  ];

  const pricing = [
    {
      plan: "3-Day Trial",
      price: "₹59",
      period: "/ 3 days",
      features: ["All Features Unlocked", "2 Users (1 Admin + 1 Kiosk)", "1 Location", "Google Drive Integration", "Real-time Control"],
      popular: false
    },
    {
      plan: "MusicSync Pro",
      price: "₹499",
      period: "/month",
      features: ["All Features Included", "2 Users (1 Admin + 1 Kiosk)", "1 Location", "Google Drive Integration", "Dayparting & Scheduling", "Remote Control & Announcements"],
      popular: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#09090b] text-white overflow-hidden">
      {/* Sticky top nav with logo */}
      <nav
        className="sticky top-0 z-50 backdrop-blur-md bg-[#09090b]/70 border-b border-white/5"
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2.5 group"
            data-testid="landing-logo"
            aria-label="MusicSync"
          >
            <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 via-pink-500 to-yellow-400 shadow-lg shadow-pink-500/30 transition-transform duration-300 group-hover:scale-105">
              <MusicNotes size={20} weight="fill" className="text-white drop-shadow" />
            </span>
            <span className="font-black tracking-tight text-xl leading-none">
              <span className="text-white">Music</span>
              <span className="bg-gradient-to-r from-blue-400 to-pink-400 bg-clip-text text-transparent">Sync</span>
            </span>
          </a>

          <div className="hidden sm:flex items-center gap-3">
            <a
              href={process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/your-demo-link'}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-book-demo"
            >
              Book Demo
            </a>
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-zinc-300 hover:text-white transition-colors"
              data-testid="nav-login"
            >
              Login
            </button>
            <button
              onClick={() => navigate('/register')}
              className="text-sm font-semibold px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-pink-500 hover:from-blue-600 hover:to-pink-600 text-white transition-all duration-300 hover:scale-105 shadow-md shadow-pink-500/20"
              data-testid="nav-register"
            >
              Start Trial
            </button>
          </div>

          <button
            onClick={() => navigate('/register')}
            className="sm:hidden text-xs font-semibold px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500 to-pink-500 text-white"
            data-testid="nav-register-mobile"
          >
            Start Trial
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 overflow-hidden" data-testid="landing-hero">
        {/* Animated Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/30 mb-8 animate-pulse">
            <Sparkle size={20} className="text-yellow-400" />
            <span className="text-sm font-mono text-blue-300">3-DAY TRIAL FOR ₹59 • ALL FEATURES INCLUDED</span>
          </div>

          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black mb-6 leading-none tracking-tight">
            <span className="bg-gradient-to-r from-blue-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent animate-gradient">
              CONTROL
            </span>
            <br />
            <span className="text-white">YOUR STORE'S</span>
            <br />
            <span className="bg-gradient-to-r from-pink-400 via-yellow-400 to-blue-400 bg-clip-text text-transparent">
              SOUNDTRACK
            </span>
          </h1>

          <p className="text-xl sm:text-2xl text-zinc-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            The only music player built for <span className="text-blue-400 font-bold">retail chains</span>. 
            Schedule dayparts, manage multiple locations, inject in-store ads - all from one powerful dashboard.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => navigate('/register?intent=trial')}
              className="bg-gradient-to-r from-blue-500 to-pink-500 hover:from-blue-600 hover:to-pink-600 text-white text-lg px-8 py-6 rounded-full border-0 shadow-lg shadow-blue-500/50 transition-all duration-300 hover:scale-105"
              data-testid="get-started-btn"
            >
              <Lightning size={24} className="mr-2" weight="fill" />
              START TRIAL — ₹59
            </Button>
            <Button
              onClick={() => navigate('/register?intent=subscription')}
              className="bg-white/5 hover:bg-white/10 text-white text-lg px-8 py-6 rounded-full border border-white/20 transition-all duration-300 hover:scale-105"
              data-testid="subscribe-direct-btn"
            >
              Subscribe — ₹499/mo
            </Button>
            <a
              href={process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/your-demo-link'}
              target="_blank"
              rel="noreferrer"
              data-testid="book-demo-landing-btn"
            >
              <Button
                className="bg-transparent hover:bg-white/5 text-white text-lg px-8 py-6 rounded-full border border-white/20"
              >
                BOOK A DEMO
              </Button>
            </a>
            <Button
              onClick={() => navigate('/login')}
              className="bg-zinc-800 hover:bg-zinc-700 text-white text-lg px-8 py-6 rounded-full border border-zinc-700"
              data-testid="login-btn"
            >
              LOGIN
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>

          <div className="mt-12 flex items-center justify-center gap-8 text-sm text-zinc-500">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-green-500" />
              <span>Try for just ₹59</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-green-500" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-green-500" />
              <span>Setup in 5 minutes</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 relative" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl sm:text-6xl font-black mb-4">
              BUILT FOR <span className="bg-gradient-to-r from-blue-400 to-pink-400 bg-clip-text text-transparent">RETAIL</span>
            </h2>
            <p className="text-xl text-zinc-400">Everything you need to create the perfect in-store experience</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="group bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-2"
                data-testid={`feature-${index}`}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-transparent to-zinc-900/50" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl sm:text-6xl font-black mb-4">
              SIMPLE <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">PRICING</span>
            </h2>
            <p className="text-xl text-zinc-400">Simple pricing, scale as you grow</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricing.map((plan) => (
              <div
                key={plan.plan}
                className={`relative bg-zinc-900 border-2 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 ${
                  plan.popular ? 'border-blue-500 shadow-xl shadow-blue-500/20' : 'border-zinc-800'
                }`}
                data-testid={`pricing-${plan.plan.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-blue-500 to-pink-500 rounded-full text-xs font-bold">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2">{plan.plan}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-black">{plan.price}</span>
                  <span className="text-zinc-500">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <CheckCircle size={20} weight="fill" className="text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate(plan.price === '₹59' ? '/register?intent=trial' : '/register?intent=subscription')}
                  className={`w-full py-6 rounded-full border-0 transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-blue-500 to-pink-500 hover:from-blue-600 hover:to-pink-600 shadow-lg shadow-blue-500/50'
                      : 'bg-zinc-800 hover:bg-zinc-700'
                  }`}
                >
                  {plan.price === '₹59' ? 'Start Trial — ₹59' : 'Subscribe — ₹499/mo'}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center text-zinc-500 text-sm">
            <p>Add locations: ₹299/month (includes 1 user) • Extra users: ₹99/month</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl sm:text-6xl font-black mb-6">
            READY TO <span className="bg-gradient-to-r from-yellow-400 to-pink-400 bg-clip-text text-transparent">TRANSFORM</span><br />YOUR STORES?
          </h2>
          <p className="text-xl text-zinc-400 mb-12">
            Join hundreds of retail chains using MusicSync to create the perfect atmosphere
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-gradient-to-r from-yellow-500 to-pink-500 hover:from-yellow-600 hover:to-pink-600 text-black text-xl font-bold px-12 py-8 rounded-full border-0 shadow-lg shadow-yellow-500/50 transition-all duration-300 hover:scale-105"
            data-testid="cta-button"
          >
            <Lightning size={28} className="mr-3" weight="fill" />
            START YOUR 3-DAY TRIAL — ₹59
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-10 px-4">
        <div className="max-w-7xl mx-auto text-center text-zinc-500 text-sm space-y-4">
          <div className="flex items-center justify-center gap-2.5 mb-2" data-testid="landing-footer-logo">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 via-pink-500 to-yellow-400 shadow-md shadow-pink-500/20">
              <MusicNotes size={18} weight="fill" className="text-white" />
            </span>
            <span className="font-black tracking-tight text-lg">
              <span className="text-white">Music</span>
              <span className="bg-gradient-to-r from-blue-400 to-pink-400 bg-clip-text text-transparent">Sync</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-wider">
            <a href="/privacy-policy" className="hover:text-blue-400 transition-colors" data-testid="landing-privacy-link">Privacy Policy</a>
            <span className="text-zinc-700">·</span>
            <a href="/terms-of-service" className="hover:text-blue-400 transition-colors" data-testid="landing-terms-link">Terms of Service</a>
            <span className="text-zinc-700">·</span>
            <a href="mailto:admin@musicsync.net" className="hover:text-blue-400 transition-colors" data-testid="landing-support-email-link">admin@musicsync.net</a>
          </div>
          <p className="text-xs text-zinc-600 max-w-3xl mx-auto leading-relaxed" data-testid="landing-legal-disclaimer">
            <strong className="text-zinc-400">Important:</strong> MusicSync is a music-player platform only. We do not provide, license, or distribute music. Subscribers are solely responsible for obtaining all required public-performance licenses (PPL, IPRS, Novex, etc.) and for playing only legally-obtained audio content. MusicSync accepts no liability for third-party copyright or licensing claims.
          </p>
          <p>© 2026 MusicSync. All rights reserved.</p>
          <div className="text-[11px] text-zinc-600 font-mono space-y-0.5" data-testid="landing-legal-business-name">
            <p>
              Operated by <span className="text-zinc-400">{LEGAL_BUSINESS_NAME}</span> · India
            </p>
            <p className="text-zinc-600" data-testid="landing-owner-email">
              Contact: <a href="mailto:admin@musicsync.net" className="text-zinc-500 hover:text-blue-400 transition-colors">admin@musicsync.net</a>
            </p>
            {(LEGAL_PAN || LEGAL_GSTIN) && (
              <p className="text-zinc-600" data-testid="landing-legal-tax-ids">
                {LEGAL_PAN && <>PAN: <span className="text-zinc-500">{LEGAL_PAN}</span></>}
                {LEGAL_PAN && LEGAL_GSTIN && <span className="mx-2">·</span>}
                {LEGAL_GSTIN && <>GSTIN: <span className="text-zinc-500">{LEGAL_GSTIN}</span></>}
              </p>
            )}
            {LEGAL_ADDRESS && (
              <p className="text-zinc-600" data-testid="landing-legal-address">
                <span className="text-zinc-500">{LEGAL_ADDRESS}</span>
              </p>
            )}
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default Landing;
